import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Trash2, Check, X, Paperclip, Settings2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, openStoredFile, formatTanggalID } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ApprovalChain } from "@/components/ApprovalChain";
import { useAuth } from "@/context/AuthContext";

const ANY = "__ANY__";
const STATUS_OPTIONS = ["Menunggu", "Disetujui", "Ditolak"];
const JENIS_OPTIONS = [
  "Cuti",
  "Izin",
  "Sakit",
  "Dinas Luar",
  "Tugas Luar",
  "Istirahat Haid",
  "Istirahat Melahirkan / Gugur Kandungan",
];

export default function AdminPengajuan() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(ANY);
  const [jenis, setJenis] = useState(ANY);
  const [detail, setDetail] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [maxMb, setMaxMb] = useState("2");
  const [working, setWorking] = useState(false);

  const params = useMemo(
    () => ({
      status: status !== ANY ? status : undefined,
      jenis: jenis !== ANY ? jenis : undefined,
    }),
    [status, jenis]
  );

  async function load() {
    setLoading(true);
    try {
      const [r, cfg] = await Promise.all([
        api.get("/requests", { params }),
        api.get("/settings/pengajuan"),
      ]);
      setRows(r.data);
      setMaxMb(String(cfg.data.max_lampiran_mb));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, jenis]);

  function refreshBadge() {
    window.dispatchEvent(new Event("atlas:refresh-approvals"));
  }

  async function approve(r) {
    if (!window.confirm(`Setujui pengajuan ${r.jenis} dari ${r.nama} (level saat ini)?`)) return;
    setWorking(true);
    try {
      await api.post(`/requests/${r.id}/approve`);
      toast.success("Disetujui");
      await load();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function reject() {
    if (rejectReason.trim().length < 3) {
      toast.error("Alasan penolakan wajib diisi (min. 3 karakter)");
      return;
    }
    setWorking(true);
    try {
      await api.post(`/requests/${rejectTarget.id}/reject`, { alasan: rejectReason.trim() });
      toast.success("Pengajuan ditolak");
      setRejectTarget(null);
      setRejectReason("");
      await load();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function del(r) {
    if (!window.confirm(`Hapus pengajuan ${r.jenis} milik ${r.nama}? Status absensi terkait akan dikembalikan.`)) return;
    try {
      await api.delete(`/requests/${r.id}`);
      toast.success("Pengajuan dihapus & absensi dikembalikan");
      await load();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function saveSettings() {
    const v = parseFloat(maxMb);
    if (!v || v <= 0 || v > 25) {
      toast.error("Batas lampiran harus antara 0.1 – 25 MB");
      return;
    }
    try {
      await api.put("/settings/pengajuan", { max_lampiran_mb: v });
      toast.success("Pengaturan disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-admin-pengajuan">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Semua Pengajuan
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Kelola workflow persetujuan, lihat semua request, dan bersihkan data tes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="pengajuan-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
        </div>
      </div>

      {/* Settings + filters */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-4">
            Filter
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1.5" data-testid="filter-req-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Semua Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Jenis</Label>
              <Select value={jenis} onValueChange={setJenis}>
                <SelectTrigger className="mt-1.5" data-testid="filter-req-jenis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Semua Jenis</SelectItem>
                  {JENIS_OPTIONS.map((j) => (<SelectItem key={j} value={j}>{j}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-4">
            <Settings2 size={12} /> Pengaturan Lampiran
          </div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Batas Ukuran Lampiran (MB)
          </Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              type="number"
              step="0.5"
              min="0.5"
              max="25"
              value={maxMb}
              onChange={(e) => setMaxMb(e.target.value)}
              data-testid="input-max-lampiran"
            />
            <Button onClick={saveSettings} className="bg-teal-700 hover:bg-teal-800" data-testid="save-settings-btn">
              Simpan
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Jenis</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Rentang</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Level</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <ClipboardList size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada pengajuan</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`admin-req-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell>
                      <div className="font-semibold text-slate-900">{r.nama}</div>
                      <div className="text-xs text-slate-500">{r.npp}</div>
                    </TableCell>
                    <TableCell><StatusBadge status={r.jenis} size="sm" /></TableCell>
                    <TableCell className="text-sm text-slate-700 whitespace-nowrap">
                      {formatTanggalID(r.tanggal_mulai)}
                      {r.tanggal_selesai !== r.tanggal_mulai ? ` — ${formatTanggalID(r.tanggal_selesai)}` : ""}
                      <span className="ml-1 text-xs text-slate-400">({r.jumlah_hari}h)</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {r.approval_chain?.length
                        ? `${Math.min(r.current_level + (r.status === "Menunggu" ? 1 : 0), r.approval_chain.length)} / ${r.approval_chain.length}`
                        : "Auto"}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status} size="sm" /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button
                        onClick={() => setDetail(r)}
                        className="text-xs font-semibold text-teal-800 hover:text-teal-900 mr-3"
                        data-testid={`req-detail-${r.id}`}
                      >
                        Detail
                      </button>
                      {r.status === "Menunggu" ? (
                        <>
                          <button
                            onClick={() => approve(r)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 mr-3"
                            data-testid={`req-approve-${r.id}`}
                          >
                            <Check size={13} /> Setujui
                          </button>
                          <button
                            onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 mr-3"
                            data-testid={`req-reject-${r.id}`}
                          >
                            <X size={13} /> Tolak
                          </button>
                        </>
                      ) : null}
                      {isAdmin ? (
                        <button
                          onClick={() => del(r)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:text-rose-900"
                          data-testid={`req-delete-${r.id}`}
                        >
                          <Trash2 size={13} /> Hapus
                        </button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="req-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detail Pengajuan</DialogTitle>
            <DialogDescription>
              {detail ? `${detail.jenis} — ${detail.nama} (${detail.npp})` : ""}
            </DialogDescription>
          </DialogHeader>
          {detail ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={detail.jenis} />
                <StatusBadge status={detail.status} />
                {detail.konversi_dari_sakit ? (
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    Sakit → Izin
                  </span>
                ) : null}
                {detail.jenis === "Izin" && detail.izin_classification ? (
                  <span
                    className="text-[10px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"
                    data-testid={`admin-klasifikasi-${detail.id}`}
                  >
                    {detail.izin_classification}
                    {detail.izin_subtype ? ` • ${detail.izin_subtype}` : ""}
                  </span>
                ) : null}
                {detail.benefit_deduction_status ? (
                  <span
                    className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${
                      detail.benefit_deduction_status === "Potong Tunjangan"
                        ? "text-rose-700 bg-rose-50 border-rose-200"
                        : "text-emerald-700 bg-emerald-50 border-emerald-200"
                    }`}
                    data-testid={`admin-benefit-${detail.id}`}
                  >
                    {detail.benefit_deduction_status}
                  </span>
                ) : null}
              </div>
              <div>
                <span className="font-semibold text-slate-900">
                  {formatTanggalID(detail.tanggal_mulai)}
                  {detail.tanggal_selesai !== detail.tanggal_mulai ? ` — ${formatTanggalID(detail.tanggal_selesai)}` : ""}
                </span>
                <span className="ml-1.5 text-xs text-slate-500">({detail.jumlah_hari} hari)</span>
              </div>
              <div className="text-slate-600">{detail.alasan}</div>
              {detail.lampiran_path ? (
                <button
                  onClick={() => openStoredFile(detail.lampiran_path).catch(() => toast.error("Gagal membuka lampiran"))}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-800 hover:text-teal-900"
                >
                  <Paperclip size={12} /> {detail.lampiran_nama || "Lihat lampiran"}
                </button>
              ) : null}
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
                  Alur Persetujuan
                </div>
                <ApprovalChain chain={detail.approval_chain} currentLevel={detail.current_level} status={detail.status} />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent className="max-w-md" data-testid="admin-reject-dialog">
          <DialogHeader>
            <DialogTitle>Tolak Pengajuan</DialogTitle>
            <DialogDescription>
              {rejectTarget ? `${rejectTarget.jenis} — ${rejectTarget.nama}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Alasan Penolakan (wajib)
            </Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="input-admin-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Batal</Button>
            <Button onClick={reject} disabled={working} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-reject-confirm-btn">
              {working ? "Memproses…" : "Tolak Pengajuan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
